import { FieldConfig } from './config';
import { ComparisonField, ComparisonRow } from './types';

export const convertToComparisonString = (val: any): string | null => {
  if (val === null || val === undefined) return null;
  if (typeof val === 'boolean') return String(val);

  if (typeof val === 'object') {
    if (val.id) return String(val.id);
    return JSON.stringify(val);
  }

  const strVal = String(val).trim();
  if (strVal === '') return null;
  const numVal = Number(strVal);
  if (!Number.isNaN(numVal) && isFinite(numVal)) {
    return String(numVal);
  }
  return strVal;
};

export const formatValueForDisplay = (
  value: any,
  fieldName: string,
  fieldConfig?: FieldConfig,
): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    if (value.id && value.name) return value.name;
    return JSON.stringify(value);
  }

  if (fieldConfig?.formatValue) {
    return fieldConfig.formatValue(value, fieldName);
  }

  return String(value);
};

const defaultMapValue = (formValue: any, _row: any) => formValue;

export const generateComparisonData = (
  formValues: any,
  rows: any[],
  fields: Record<string, FieldConfig>,
  excludeKeys: string[] = [],
): ComparisonRow[] => {
  if (!formValues || !rows) return [];

  const formValueKeys = Object.keys(formValues).filter(
    (key) =>
      !excludeKeys.includes(key) &&
      formValues[key] !== undefined &&
      formValues[key] !== null &&
      formValues[key] !== '',
  );

  return rows.map((row) => {
    const comparisonFields: ComparisonField[] = [];

    formValueKeys.forEach((fieldName) => {
      const fieldConfig = fields[fieldName];
      if (fieldConfig?.excludeFromComparison) return;

      const mapValue = fieldConfig?.mapValue || defaultMapValue;
      const mappedValue = mapValue(formValues[fieldName], row);

      if (mappedValue === undefined || mappedValue === null) return;

      const fieldsToCompare: Array<{ name: string; value: any }> = [];
      if (
        typeof mappedValue === 'object' &&
        !Array.isArray(mappedValue) &&
        mappedValue !== null
      ) {
        Object.entries(mappedValue).forEach(([key, value]) => {
          fieldsToCompare.push({ name: key, value });
        });
      } else {
        fieldsToCompare.push({ name: fieldName, value: mappedValue });
      }

      fieldsToCompare.forEach(({ name: compareFieldName, value: newValue }) => {
        const oldValue = row[compareFieldName];

        if (newValue === undefined || newValue === null || newValue === '')
          return;

        const oldComparisonString = convertToComparisonString(oldValue);
        const newComparisonString = convertToComparisonString(newValue);

        if (oldComparisonString === null && newComparisonString === null)
          return;
        if (oldComparisonString === newComparisonString) return;

        comparisonFields.push({
          fieldName: compareFieldName,
          oldValue,
          newValue,
          hasChanged: true,
        });
      });
    });

    return {
      rowId: row.id,
      row,
      fields: comparisonFields,
    };
  });
};

export const hasChanges = (comparisonData: ComparisonRow[]): boolean => {
  return comparisonData.some((row) =>
    row.fields.some((field) => field.hasChanged),
  );
};
